import React, { useState, useEffect } from 'react';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, getDoc, doc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, ArrowLeft } from 'lucide-react';
import AuthForm from './AuthForm';
import '../Login.css';

function ClientLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem('client_email');
    const savedPassword = localStorage.getItem('client_password');
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Sauvegarder les identifiants si "Se souvenir de moi" est coché
      if (rememberMe) {
        localStorage.setItem('client_email', email);
        localStorage.setItem('client_password', password);
      } else {
        localStorage.removeItem('client_email');
        localStorage.removeItem('client_password');
      }

      // Vérifier si l'utilisateur est un bénéficiaire via la base de données
      const db = getFirestore();

      console.log('🔍 Vérification du bénéficiaire pour UID:', user.uid);

      // 1. Vérifier le rôle dans la collection employees (avec tentative de retry)
      let employeeDoc = await getDoc(doc(db, 'employees', user.uid));

      // Tentative de retry si le document n'est pas trouvé (micro-latence Cloud Functions)
      if (!employeeDoc.exists()) {
        console.log('⏳ Document employee non trouvé, tentative de retry (1/2)...');
        await new Promise(resolve => setTimeout(resolve, 1500));
        employeeDoc = await getDoc(doc(db, 'employees', user.uid));
      }

      if (!employeeDoc.exists()) {
        console.log('⏳ Document employee non trouvé, tentative de retry (2/2)...');
        await new Promise(resolve => setTimeout(resolve, 1500));
        employeeDoc = await getDoc(doc(db, 'employees', user.uid));
      }

      console.log('👤 Document employee existe:', employeeDoc.exists());

      if (employeeDoc.exists() && employeeDoc.data().role === 'beneficiaire') {
        // 2. Récupérer le clientId depuis la collection clients
        let clientsQuery = query(
          collection(db, 'clients'),
          where('authUid', '==', user.uid),
          limit(1)
        );
        let clientsSnapshot = await getDocs(clientsQuery);

        // Retry pour le document client également si nécessaire
        if (clientsSnapshot.empty) {
          console.log('⏳ Client non trouvé par authUid, tentative de retry...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          clientsSnapshot = await getDocs(clientsQuery);
        }

        console.log('📄 Clients trouvés:', clientsSnapshot.size);

        if (!clientsSnapshot.empty) {
          const clientId = clientsSnapshot.docs[0].id;
          console.log('✅ Redirection vers dashboard client:', clientId);
          navigate(`/client-dashboard/${clientId}`);
        } else {
          console.error('❌ Aucun client trouvé avec authUid:', user.uid);
          setError('Compte bénéficiaire non trouvé dans la base de données.');
          await auth.signOut();
        }
      } else {
        console.error('❌ Accès refusé - pas un bénéficiaire');
        setError('Accès réservé aux bénéficiaires. Veuillez utiliser le portail intervenant.');
        await auth.signOut();
      }
    } catch (error) {
      setError('Email ou mot de passe incorrect');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthForm title="Connexion Bénéficiaire">
      <Link to="/" className="back-button">
        <ArrowLeft size={18} />
        Retour
      </Link>

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="input-group">
          <label htmlFor="email" className="input-label">
            <Mail className="input-icon" />
            <span>Email bénéficiaire</span>
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="input-field"
            placeholder="votre.email@client.fr"
          />
        </div>

        <div className="input-group">
          <label htmlFor="password" className="input-label">
            <Lock className="input-icon" />
            <span>Mot de passe</span>
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="input-field"
            placeholder="Votre mot de passe"
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="remember-me">
          <input
            type="checkbox"
            id="rememberMe"
            className="remember-me-checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          <label htmlFor="rememberMe" className="remember-me-label">
            Se souvenir de moi
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="auth-button"
        >
          {isLoading ? 'Connexion...' : 'Se connecter'}
        </button>

        <div className="auth-footer">
          <p className="text-sm text-gray-500">
            Vous êtes intervenant ?{' '}
            <Link to="/login" className="text-blue-600 hover:underline">
              Connectez-vous ici
            </Link>
          </p>
        </div>
      </form>
    </AuthForm>
  );
}

export default ClientLogin;
